import React from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import AnalyticsHeader from "visual-components/analyticsDashboard/analytics-header";
import Donut from "visual-components/util/graphs/donut";
import { Small } from "visual-components/util/texts";

/**
 * Brand Name Legend Item
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {string} props.color - Color box color
 * @param {string} props.title - Brand title
 * @param {string} props.name - Brand name
 * @param {string} [props.id] - Optional brand ID
 * @param {boolean} props.selected - Whether the brand is selected
 * @returns {import("react").ReactElement}
 */
function BrandName(props) {
  const { onClick, color, title, name, id, selected } = props;

  return (
    <Box
      sx={{
        width: 117,
        mb: 0.75,
        cursor: "pointer",
        opacity: selected ? 1 : 0.5,
        height: 11,
        display: "inline-block",
        position: "relative",
      }}
      onClick={() => onClick(id || name)}
    >
      <Box
        sx={{
          position: "absolute",
          width: 12,
          height: 12,
          backgroundColor: color,
        }}
      />
      <Box sx={{ position: "absolute", left: 20, top: 1 }}>
        <Small>{title && title.length > 15 ? title.substring(0, 15) + "..." : title}</Small>
      </Box>
    </Box>
  );
}

/**
 * Promo Link Card Component
 * Displays entries by channel as a donut chart
 * @param {Object} props
 * @param {Array} props.chartOptions - Legend options
 * @param {Array<{value: string, label: string}>} props.brandOptions - Dropdown options
 * @param {string} props.value - Selected dropdown value
 * @param {Function} props.onChange - Dropdown change handler
 * @param {Array} props.graphData - Donut chart data
 * @param {Object} props.hideMap - Map of hidden items
 * @param {Function} props.toggle - Toggle item visibility
 * @param {Object} props.colorMap - Color mapping for chart
 * @param {boolean} [props.isSolo] - Whether to hide dropdown
 * @param {string} [props.className] - Additional CSS class
 * @returns {import("react").ReactElement}
 */
export default function PromoLinkCard(props) {
  const {
    chartOptions,
    brandOptions,
    value,
    onChange,
    graphData,
    hideMap,
    toggle,
    colorMap,
    isSolo,
    className = "",
  } = props;

  const promoOptions = chartOptions.map((item, idx) => (
    <BrandName {...item} onClick={toggle} selected={!hideMap[item.id]} key={idx} />
  ));

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader
        title={
          <AnalyticsHeader
            title="Entries by Channel"
            showSelect={!isSolo}
            selectProps={{ value: value, options: brandOptions, onChange: onChange }}
          />
        }
      />
      <CardContent>
        <Box sx={{ width: 270, mt: 2.5, position: "relative" }}>
          <Box sx={{ display: "flex" }}>
            <Box>{promoOptions}</Box>
            <Box
              sx={{
                top: 12,
                left: 80,
                width: 100,
                height: 100,
              }}
            >
              <Donut colorMap={colorMap} items={graphData} hideMap={hideMap} />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
